import {
  AfterContentInit,
  ChangeDetectorRef,
  Component,
  ContentChildren,
  EventEmitter,
  forwardRef,
  HostListener,
  Input,
  Output,
  Provider,
  QueryList
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR } from '@angular/forms';
import { OPTION_PARENT_COMPONENT, OptionComponent } from '../option/option.component';
import { DomSanitizer, SafeHtml } from '@angular/platform-browser';

export const SELECT_CONTROL_VALUE_ACCESSOR: Provider = {
  provide: NG_VALUE_ACCESSOR,
  useExisting: forwardRef(() => SelectComponent),
  multi: true
};

export const OPTION_PARENT: Provider = {
  provide: OPTION_PARENT_COMPONENT,
  useExisting: forwardRef(() => SelectComponent)
};

const UP_ARROW = 38;
const DOWN_ARROW = 40;
const ENTER = 13;
const END = 35;
const HOME = 36;

@Component({
  selector: 'gcc-select',
  templateUrl: './select.component.html',
  styleUrls: ['./select.component.scss'],
  providers: [OPTION_PARENT, SELECT_CONTROL_VALUE_ACCESSOR]
})
export class SelectComponent implements AfterContentInit, ControlValueAccessor {
  @ContentChildren(OptionComponent, { descendants: true }) public options: QueryList<OptionComponent>;
  @Input() public placeholder: string;
  @Input() public multiple = false;
  @Input()
  get value(): any {
    return this._value;
  }
  set value(newValue: any) {
    if (newValue !== this._value) {
      this.writeValue(newValue);
      this._value = newValue;
    }
  }

  get selected(): OptionComponent | OptionComponent[] {
    return this.options && this.multiple
      ? this.options.filter(i => i.selected)
      : this.options.filter(i => i.selected)[0];
  }

  get empty(): boolean {
    return this.multiple ? !(this.selected as OptionComponent[]).length : !this.selected;
  }

  get triggerValue(): SafeHtml[] | string[] {
    if (this.multiple) {
      return (this.selected as OptionComponent[]).map(({ viewValue }) =>
        this.sanitizer.bypassSecurityTrustHtml(viewValue)
      );
    }

    return [this.sanitizer.bypassSecurityTrustHtml((this.selected as OptionComponent).viewValue)];
  }

  @Output() public valueChange: EventEmitter<any> = new EventEmitter<any>();

  public disabled: boolean;
  public isOpen: boolean;

  private _value: any;

  constructor(private _changeDetectorRef: ChangeDetectorRef, private sanitizer: DomSanitizer) {}

  public ngAfterContentInit(): void {
    if (this.value) {
      // For avoiding error ExpressionChangedAfterItHasBeenCheckedError postpone operation to microtask  after form initialization
      Promise.resolve().then(() => this._setSelectionByValue(this._value));
    }
  }

  public writeValue(value: any): void {
    if (this.options) {
      this._setSelectionByValue(value);
    } else {
      this._value = value;
    }
  }

  public registerOnChange(fn: (value: any) => void): void {
    this._onChange = fn;
  }

  public registerOnTouched(fn: () => {}): void {
    this._onTouched = fn;
  }
  public setDisabledState(isDisabled: boolean): void {
    this.disabled = isDisabled;
  }

  public handleDropdownToggle(isOpen: boolean) {
    if (isOpen) {
      if (!this.multiple && this.selected) {
        (this.selected as OptionComponent).setActiveStyles();
      }
    } else {
      this._onTouched();
      this.options.forEach(o => o.setInactiveStyles());
    }
    this.isOpen = isOpen;
  }

  @HostListener('keydown', ['$event'])
  public handleKeyDown(event: KeyboardEvent) {
    if (this.isOpen) {
      // tslint:disable-next-line: deprecation
      const keyCode = event.keyCode;
      const isArrowKey = keyCode === DOWN_ARROW || keyCode === UP_ARROW;
      const activeIndex = this.options.toArray().findIndex(opt => opt.active);
      if (keyCode === HOME || keyCode === END) {
        event.preventDefault();
        this.options.forEach(opt => opt.setInactiveStyles());
        keyCode === HOME
          ? this.options.toArray()[0].setActiveStyles()
          : this.options.toArray()[this.options.length - 1].setActiveStyles();
      } else if (isArrowKey && !hasModifierKey(event)) {
        event.preventDefault();

        let nextIndex = activeIndex + (keyCode === DOWN_ARROW ? 1 : -1);
        if (nextIndex < 0 || nextIndex > this.options.length - 1) {
          nextIndex = activeIndex;
        }
        if (activeIndex > -1) {
          this.options.toArray()[activeIndex].setInactiveStyles();
        }
        this.options.toArray()[nextIndex].setActiveStyles();
      } else if (keyCode === ENTER && activeIndex !== -1 && !hasModifierKey(event)) {
        event.preventDefault();
        this.isOpen = false;
        this.options.toArray()[activeIndex].selectViaInteraction();
      }
    }
  }

  public toggle(opt: OptionComponent): void {
    if (!this.multiple) {
      this.options.forEach(o => o.deselect());
    }
    opt.selected ? opt.deselect() : opt.select();
    this._propagateChanges();
    this._changeDetectorRef.markForCheck();
  }

  private _setSelectionByValue(value: any | any[]): void {
    this.options.forEach(opt => opt.deselect());

    if (this.multiple && value) {
      if (!Array.isArray(value)) {
        throw getSelectNonArrayValueError();
      }

      value.forEach((currentValue: any) => this._selectValue(currentValue));
    } else {
      this._selectValue(value);
    }

    this._changeDetectorRef.markForCheck();
  }

  private _selectValue(value: any): void {
    const correspondingOption = this.options.find((option: OptionComponent) => {
      return option.value === value;
    });

    if (correspondingOption) {
      correspondingOption.select();
    }
  }

  private _propagateChanges(): void {
    let valueToEmit: any = null;

    if (this.multiple) {
      valueToEmit = (this.selected as OptionComponent[]).map(option => option.value);
    } else {
      valueToEmit = this.selected ? (this.selected as OptionComponent).value : null;
    }

    this._value = valueToEmit;
    this.valueChange.emit(valueToEmit);
    this._onChange(valueToEmit);
  }

  private _onChange: (value: any) => void = () => {};
  private _onTouched = () => {};
}

export function getSelectNonArrayValueError(): Error {
  return Error('Value must be an array in multiple-selection mode.');
}

export function hasModifierKey(event: KeyboardEvent): boolean {
  return event.altKey || event.shiftKey || event.ctrlKey || event.metaKey;
}
